// FE-SCRM-059 — Rich body for Sales Activity rows.
//
// Replaces the bare "<type chip> <subject>" pattern with:
//
//   [actor badge] [type icon] [verb copy] — <subject>
//
// System-generated rows (`bor_letter_generated`, `proposal_delivered`,
// `win_loss_interview`) render the "BeneCRM" actor instead of a
// resolved teammate and pick up a subtle background tint via the
// `data-system="true"` attribute (consumers style off the data
// attribute rather than coupling to a className).
//
// Actor resolution is injected via `resolveActorName(member_id) →
// display_name | null`. The host (ActivityListTable / ActivityRail)
// loads a team-member list once and passes a memoised lookup so we
// don't refetch per row. Unresolved or null member ids fall back to
// "Teammate" — never bleed the raw uuid into the UI.

import {
  BookOpen,
  Calendar,
  CheckCheck,
  ClipboardList,
  FileCheck2,
  FileSearch,
  FileText,
  FileUp,
  Inbox,
  Mail,
  MessageSquare,
  Phone,
  ShieldCheck,
  Trophy,
  Users,
} from "lucide-react";

import type { ActivityType, SalesActivity } from "./types";


export type ActorResolver = (member_id: string) => string | null;


export interface ActivityRowRichBodyProps {
  activity: SalesActivity;
  resolveActorName: ActorResolver;
}


// Activity types whose owner is always the system, never a producer.
// Per FE-SCRM-059 spec these get the "BeneCRM" actor badge and the
// system-tinted row background.
const SYSTEM_TYPES: ReadonlySet<ActivityType> = new Set<ActivityType>([
  "bor_letter_generated",
  "bor_letter_sent",
  "proposal_delivered",
  "win_loss_interview",
]);


// Verb copy keyed by activity_type. Matches the spec line: "Owen
// logged a call" / "scheduled a meeting" / etc. System rows use a
// generated-by phrasing.
const VERB_COPY: Record<ActivityType, string> = {
  note: "left a note",
  call: "logged a call",
  email: "logged an email",
  meeting: "scheduled a meeting",
  follow_up: "scheduled a follow-up",
  discovery_call: "logged a discovery call",
  census_request: "sent a census request",
  census_received: "received the census",
  rfp_sent: "sent an RFP",
  proposals_received: "received proposals",
  carrier_meeting: "logged a carrier meeting",
  proposal_delivered: "delivered a proposal",
  finalist_presentation: "logged a finalist presentation",
  bor_letter_generated: "generated a BOR letter",
  bor_letter_sent: "sent a BOR letter",
  decision_meeting: "logged a decision meeting",
  contract_executed: "logged the contract execution",
  win_loss_interview: "scheduled a win/loss interview",
};


const ICON_BY_TYPE: Record<ActivityType, React.ComponentType<{
  className?: string; "aria-hidden"?: boolean | "true" | "false";
}>> = {
  note: MessageSquare,
  call: Phone,
  email: Mail,
  meeting: Users,
  follow_up: Calendar,
  discovery_call: BookOpen,
  census_request: FileSearch,
  census_received: Inbox,
  rfp_sent: FileUp,
  proposals_received: FileText,
  carrier_meeting: Users,
  proposal_delivered: FileCheck2,
  finalist_presentation: ClipboardList,
  bor_letter_generated: ShieldCheck,
  bor_letter_sent: ShieldCheck,
  decision_meeting: ClipboardList,
  contract_executed: Trophy,
  win_loss_interview: CheckCheck,
};


function ActorBadge({
  actorLabel, isSystem,
}: {
  actorLabel: string;
  isSystem: boolean;
}) {
  return (
    <span
      data-testid="activity-actor-badge"
      data-system={isSystem ? "true" : "false"}
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5",
        "text-xs font-medium",
        isSystem
          ? "bg-teal/10 text-teal"
          : "bg-recessed text-text-primary",
      ].join(" ")}
    >
      <span
        aria-hidden
        className={[
          "inline-flex h-4 w-4 items-center justify-center rounded-full",
          "text-[10px] font-semibold",
          isSystem
            ? "bg-teal text-white"
            : "bg-text-tertiary text-white",
        ].join(" ")}
      >
        {actorLabel.charAt(0).toUpperCase()}
      </span>
      {actorLabel}
    </span>
  );
}


export function ActivityRowRichBody({
  activity, resolveActorName,
}: ActivityRowRichBodyProps): JSX.Element {
  const isSystem = SYSTEM_TYPES.has(activity.type as ActivityType);
  const Icon = ICON_BY_TYPE[activity.type as ActivityType]
    ?? MessageSquare;
  const verb = VERB_COPY[activity.type as ActivityType]
    ?? "logged activity";

  let actorLabel: string;
  if (isSystem) {
    actorLabel = "BeneCRM";
  } else if (activity.owner_id) {
    actorLabel = resolveActorName(activity.owner_id) ?? "Teammate";
  } else {
    actorLabel = "Teammate";
  }

  return (
    <div
      data-testid="activity-rich-body"
      data-system={isSystem ? "true" : "false"}
      className={[
        "flex items-center gap-2 text-sm",
        isSystem ? "bg-teal/5 rounded px-2 py-1" : "",
      ].join(" ")}
    >
      <ActorBadge actorLabel={actorLabel} isSystem={isSystem} />
      <span
        data-testid={`activity-type-icon-${activity.type}`}
        className="inline-flex h-4 w-4 items-center text-text-tertiary"
      >
        <Icon aria-hidden className="h-4 w-4" />
      </span>
      <span className="text-text-secondary">{verb}</span>
      {activity.subject ? (
        <>
          <span className="text-text-tertiary">—</span>
          <span className="truncate font-medium text-text-primary">
            {activity.subject}
          </span>
        </>
      ) : null}
    </div>
  );
}
